import { memo } from 'react';
import { Avatar, Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CampaignIcon from '@mui/icons-material/Campaign';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import PdfIcon from 'assets/icons/pdf.svg';
import DocIcon from 'assets/icons/doc.svg';
import XlsIcon from 'assets/icons/xls.svg';
import { useTranslation } from 'react-i18next';
import { InventoryDownloadService } from 'modules/export/services';
import { INotification, FileAttachment } from '../../interfaces/INotification';

interface NotificationExportItemProps {
  notification: INotification;
  onMarkAsRead?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const NotificationExportItem = memo(({ notification/*, onMarkAsRead */, onDelete }: NotificationExportItemProps) => {
  const { t } = useTranslation('notification');
  const relativeTime = formatDistanceToNow(new Date(notification?.createdAt || new Date()), {
    addSuffix: true,
    locale: es,
  });

  const handleDownload = (file: FileAttachment) => {
    const downloadId = notification.data?._id || notification.data?.path;
    if (!downloadId) {
      return;
    }

    const filename = notification.data?.name || file.filename || 'export_file';
    const lower = filename.toLowerCase();

    // Reuse the same service logic as in export modals
    if (lower.endsWith('.pdf')) {
      void InventoryDownloadService._downloadFileToPDF(downloadId, filename);
      return;
    }

    void InventoryDownloadService._downloadFileToExcel(downloadId, filename);
  };

  const buildFileFromNotification = (currentNotification: INotification): FileAttachment | null => {
    if (currentNotification.data?.file) {
      return currentNotification.data.file;
    }

    const data = currentNotification.data;
    const rawPath = data.path || undefined;
    if (!rawPath) {
      return null;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
    const relativePath = rawPath.startsWith('/') ? rawPath : `/ms-inventory/api/download/download/${rawPath}`;
    const url = `${baseUrl}${relativePath}`;
    const filename = data.name || currentNotification.message || 'export_file';

    const lower = filename.toLowerCase();
    let type: string = 'application/octet-stream';
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
      type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    } else if (lower.endsWith('.pdf')) {
      type = 'application/pdf';
    } else if (lower.endsWith('.docx') || lower.endsWith('.doc')) {
      type = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    } else if (lower.endsWith('.csv')) {
      type = 'text/csv';
    }

    return {
      url,
      filename,
      type,
    };
  };

  const file: FileAttachment | null = buildFileFromNotification(notification);
  const files: FileAttachment[] = file ? [file] : [];

  const getFileIcon = (type: string) => {
    const lowerType = type.toLowerCase();

    if (
      lowerType.includes('spreadsheet') ||
      lowerType.includes('excel') ||
      lowerType.includes('xlsx') ||
      lowerType.includes('xls') ||
      lowerType.includes('csv')
    ) {
      return <img src={XlsIcon} alt='xls' style={{ width: 24, height: 24 }} />;
    }
    if (lowerType.includes('pdf')) {
      return <img src={PdfIcon} alt='pdf' style={{ width: 24, height: 24 }} />;
    }
    if (lowerType.includes('word') || lowerType.includes('docx') || lowerType.includes('doc')) {
      return <img src={DocIcon} alt='doc' style={{ width: 24, height: 24 }} />;
    }
    return '📁';
  };

  // const getFileSize = (url: string) => {
  //   return 'N/A';
  // };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        transition: 'background-color 0.2s',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
      id={notification._id}
      data-notification-id={notification?._id}
    >
      <Stack direction='row' spacing={2}>
        <Box position='relative'>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
            }}
          >
            <CampaignIcon />
          </Avatar>
          {!notification.viewed && (
            <FiberManualRecordIcon
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                fontSize: 14,
                color: 'success.main',
                bgcolor: 'white',
                borderRadius: '50%',
              }}
            />
          )}
        </Box>

        <Box flex={1}>
          <Stack spacing={1}>
            <Typography variant='body2'>
              <Typography component='span' variant='body2' color='text.primary' fontWeight={600}>
                {t('titles.EXPORT_DONE')}
              </Typography>
            </Typography>

            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='caption' color='text.secondary'>
                {relativeTime}
              </Typography>
            </Stack>

            <Stack spacing={1} mt={1}>
              {files.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' alignItems='center' spacing={1.5}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: 'primary.lighter',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.25rem',
                        }}
                      >
                        {getFileIcon(file.type)}
                      </Box>
                      <Box>
                        <Typography variant='body2' fontWeight={500}>
                          {file.filename}
                        </Typography>
                        {/* <Typography variant='caption' color='text.secondary'> */}
                        {/*  {getFileSize(file.url)} */}
                        {/* </Typography> */}
                      </Box>
                    </Stack>
                    <IconButton
                      size='small'
                      onClick={() => {
                        handleDownload(file);
                      }}
                    >
                      <CloudDownloadOutlinedIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>

        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(notification._id);
          }}
          sx={{
            alignSelf: 'flex-start',
            mt: 0.5,
            opacity: 0.7,
            '&:hover': {
              opacity: 1,
              bgcolor: 'error.light',
              color: 'white',
            },
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>
    </Paper>
  );
});

NotificationExportItem.displayName = 'NotificationExportItem';
